import { onBeforeUnmount, onMounted, ref } from 'vue';

/** How urgently a screen reader should speak an announcement. */
export type NxAnnouncePoliteness = 'polite' | 'assertive';

export interface NxAnnounceOptions {
  /** `'polite'` waits for the reader to finish; `'assertive'` interrupts. Default `'polite'`. */
  politeness?: NxAnnouncePoliteness;
}

/**
 * The text of the two shared live regions. `NxLiveAnnouncer` renders them;
 * without a mounted announcer the lazy fallback region below mirrors them.
 */
export const nxAnnouncerMessages = {
  polite: ref(''),
  assertive: ref(''),
};

/** Invisible toggle suffix: a repeated identical message must still change the region's text. */
const REPEAT_MARK = ' ';

const hostStack = ref<symbol[]>([]);
let fallback: HTMLElement | null = null;
let fallbackFresh = false;
const pending = new Set<ReturnType<typeof setTimeout>>();

function regionText(politeness: NxAnnouncePoliteness, text: string): string {
  const current = nxAnnouncerMessages[politeness].value;
  // Screen readers only speak a live region when its text CHANGES — the same
  // "Gespeichert" twice in a row would be silent the second time. Toggling an
  // invisible trailing space makes the second one a change.
  if (current.replace(REPEAT_MARK, '') === text) {
    return current.endsWith(REPEAT_MARK) ? text : text + REPEAT_MARK;
  }
  return text;
}

function createFallback(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const root = document.createElement('div');
  root.dataset.nxAnnouncerFallback = '';
  // Inline styles, not a class: the fallback lives outside any component and
  // so outside the kit's scoped styles.
  root.setAttribute(
    'style',
    'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0'
  );
  for (const politeness of ['polite', 'assertive'] as const) {
    const region = document.createElement('div');
    region.setAttribute('aria-live', politeness);
    region.setAttribute('aria-atomic', 'true');
    region.dataset.nxAnnouncerRegion = politeness;
    root.append(region);
  }
  document.body.append(root);
  fallbackFresh = true;
  return root;
}

function writeFallback(politeness: NxAnnouncePoliteness, text: string): void {
  fallback ??= createFallback();
  const region = fallback?.querySelector<HTMLElement>(`[data-nx-announcer-region="${politeness}"]`);
  if (!region) return;
  if (!fallbackFresh) {
    region.textContent = text;
    return;
  }
  // A live region that appears in the same moment as its text is often not
  // spoken: readers only watch regions that already exist. Give the brand-new
  // region a beat before the first message lands in it.
  fallbackFresh = false;
  const timer = globalThis.setTimeout(() => {
    pending.delete(timer);
    region.textContent = text;
  }, 100);
  pending.add(timer);
}

/**
 * Speaks `text` through the app's shared live region. Works anywhere — in a
 * component, a store or a plain function — and falls back to a lazily created
 * region while no `NxLiveAnnouncer` is mounted. Empty text is ignored.
 */
export function nxAnnounce(text: string, options: NxAnnounceOptions = {}): void {
  const trimmed = text.trim();
  if (!trimmed) return;
  const politeness = options.politeness ?? 'polite';
  const next = regionText(politeness, trimmed);
  nxAnnouncerMessages[politeness].value = next;
  if (hostStack.value.length === 0) writeFallback(politeness, next);
}

/** `const { announce } = useNxAnnounce()` — the composable face of {@link nxAnnounce}. */
export function useNxAnnounce(): { announce: typeof nxAnnounce } {
  return { announce: nxAnnounce };
}

function removeFallback(): void {
  fallback?.remove();
  fallback = null;
  fallbackFresh = false;
  for (const timer of pending) globalThis.clearTimeout(timer);
  pending.clear();
}

/**
 * Host coordination, like the toast host: with nested announcers only the most
 * recently mounted one renders the regions, so nothing is spoken twice. A
 * mounted host also retires the fallback region.
 */
export function useNxAnnouncerPresence(): { isActive: () => boolean } {
  const token = Symbol('nx-announcer');
  onMounted(() => {
    hostStack.value = [...hostStack.value, token];
    removeFallback();
  });
  onBeforeUnmount(() => {
    hostStack.value = hostStack.value.filter((t) => t !== token);
  });
  return { isActive: () => hostStack.value.at(-1) === token };
}

/** Clears messages, hosts and the fallback region (tests / HMR teardown). */
export function resetNxAnnouncer(): void {
  nxAnnouncerMessages.polite.value = '';
  nxAnnouncerMessages.assertive.value = '';
  hostStack.value = [];
  removeFallback();
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => resetNxAnnouncer());
}
